import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { PackId, CardPack, Joker, ConsumableCard } from '../../engine/types'
import { ALL_PACKS, POKER_CONFIG, JOKER_PRICE, SUIT_DISPLAY, STAKEHOLDER_TO_SUIT } from '../../data/pokerConfig'
import { getIconifyUrl } from '../../data/cardIcons'
import { useContractGameStore } from '../../store/useContractGameStore'
import { PACK_IMAGE, PACK_THEME } from '../../data/packTheme'
import { CardShell, CARD_SIZES } from '../components'
import { JokerCard } from '../cards/JokerCard'
import { JokerRow } from '../cards/JokerRow'
import { PokerLayout } from '../layout/PokerLayout'
import { DeckPile } from '../cards/DeckPile'
import { useDeckInfo } from '../../hooks/useDeckInfo'
import { cn } from '../utils/cn'

export function PackShop() {
  const { t } = useTranslation()
  const dispatch = useContractGameStore((s) => s.dispatch)
  const purchasedPacks = useContractGameStore((s) => s.purchasedPacks)
  const consumables = useContractGameStore((s) => s.consumables)
  const shopMoney = useContractGameStore((s) => s.shopMoney)
  const equippedJokers = useContractGameStore((s) => s.equippedJokers)
  const jokerPool = useContractGameStore((s) => s.jokerPool)
  const isInterShop = useContractGameStore((s) => s.isInterShop)
  const maxJokers = useContractGameStore((s) => s.maxJokers)
  const blinds = useContractGameStore((s) => s.blinds)
  const currentBlindIndex = useContractGameStore((s) => s.currentBlindIndex)
  const consumableHand = useContractGameStore((s) => s.consumableHand)
  const { deckSize, totalSize } = useDeckInfo()
  const [openPackId, setOpenPackId] = useState<PackId | null>(null)
  const [actionInProgress, setActionInProgress] = useState(false)

  const guardedDispatch = useCallback((action: Parameters<typeof dispatch>[0]) => {
    if (actionInProgress) return
    setActionInProgress(true)
    dispatch(action)
    // Reset after a short debounce
    setTimeout(() => setActionInProgress(false), 300)
  }, [dispatch, actionInProgress])

  // During inter-shop: show actual consumableHand; initial shop: show purchased consumables
  const displayConsumables: ConsumableCard[] = isInterShop ? consumableHand : consumables

  const canReroll = shopMoney >= POKER_CONFIG.REROLL_COST

  const handleSelectCard = (packId: PackId, consumableId: string) => {
    dispatch({ type: 'SELECT_FROM_PACK', packId, consumableId })
    setOpenPackId(null)
  }

  const handleBuyJoker = (jokerId: string) => {
    guardedDispatch({ type: 'BUY_JOKER', jokerId })
  }

  const handleReroll = () => {
    guardedDispatch({ type: 'REROLL_JOKERS' })
  }

  // Sidebar header: shop marquee sign
  const shopMarquee = (
    <div
      className="mx-3 mt-4 mb-3 rounded-lg relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #8B2020, #A52828, #8B2020)',
        border: '3px solid #C9A96E',
        boxShadow: '0 0 20px rgba(201,169,110,0.3), inset 0 0 20px rgba(0,0,0,0.3)',
        padding: '12px 8px',
      }}
    >
      {/* Decorative bulbs */}
      <div className="absolute top-0 left-0 right-0 flex justify-around px-1" style={{ transform: 'translateY(-3px)' }}>
        {[0, 1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            className="w-[6px] h-[6px] rounded-full"
            style={{
              background: i % 2 === 0 ? '#FFE066' : '#FF9933',
              boxShadow: `0 0 4px ${i % 2 === 0 ? '#FFE066' : '#FF9933'}`,
              animation: `bulb-flicker 3s ease-in-out ${i * 0.4}s infinite`,
            }}
          />
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex justify-around px-1" style={{ transform: 'translateY(3px)' }}>
        {[0, 1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            className="w-[6px] h-[6px] rounded-full"
            style={{
              background: i % 2 === 1 ? '#FFE066' : '#FF9933',
              boxShadow: `0 0 4px ${i % 2 === 1 ? '#FFE066' : '#FF9933'}`,
              animation: `bulb-flicker 3s ease-in-out ${i * 0.4 + 0.2}s infinite`,
            }}
          />
        ))}
      </div>
      <div
        className="text-center font-black text-2xl tracking-[4px]"
        style={{
          fontFamily: "var(--font-display)",
          color: '#FFE8A0',
          animation: 'marquee-glow 3s ease-in-out infinite',
        }}
      >
        {t('shop.title')}
      </div>
    </div>
  )

  return (
    <PokerLayout sidebarHeader={shopMarquee}>
      {/* Top row: Jokers (left) + Consumables (right) */}
      <div className="relative z-10 flex items-start">
        <div className="flex-1 min-w-0">
          <JokerRow jokers={equippedJokers} maxJokers={maxJokers} />
        </div>
        <ShopConsumableSlots consumables={displayConsumables} />
      </div>

      {/* TWO-TIER SHOP AREA */}
      <div
        className="flex-1 flex flex-col justify-center gap-3 md:gap-5 px-2 md:px-5 py-2 md:py-4 ml-1 md:ml-4 mr-2 md:mr-[164px] mt-4 md:mt-12 mb-3 relative z-10 rounded-xl"
        style={{
          background: 'rgba(0,0,0,0.15)',
          border: '2px solid rgba(201,169,110,0.5)',
        }}
      >

        {/* === UPPER TIER: Joker purchase area === */}
        <div
          className="rounded-xl p-5"
          style={{
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(160,120,200,0.15)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[10px] uppercase tracking-[2px] font-bold" style={{ color: 'rgba(200,170,230,0.5)' }}>
              Joker
            </div>
            <div className="flex-1 h-px" style={{ background: 'rgba(160,120,200,0.1)' }} />
          </div>

          <div className="flex items-start gap-4">
            {/* Left: Proceed + Reroll buttons */}
            <div className="flex flex-col gap-2.5 shrink-0" style={{ width: 140 }}>
              <button
                className="flex-1 rounded-lg font-bold text-[14px] border-none cursor-pointer transition-all duration-300"
                disabled={actionInProgress}
                onClick={() => guardedDispatch({ type: isInterShop ? 'CONTINUE_TO_NEXT_BLIND' : 'START_CHALLENGE' })}
                style={{
                  background: 'linear-gradient(135deg, #3D8B4F, #2D7040)',
                  color: '#D4FFDD',
                  boxShadow: '0 4px 16px rgba(61,139,79,0.4), inset 0 1px 0 rgba(255,255,255,0.15)',
                  border: '2px solid rgba(100,200,120,0.3)',
                  animation: 'gentle-bounce 2s ease-in-out infinite',
                  minHeight: 42,
                }}
              >
                {isInterShop
                  ? (blinds[currentBlindIndex]
                    ? t('blind.nextBlind', { label: blinds[currentBlindIndex].label })
                    : t('shop.enterChallenge'))
                  : t('shop.enterChallenge')}
                <img src={getIconifyUrl('lucide:arrow-right', 16, '#D4FFDD')} alt="" aria-hidden="true" className="w-4 h-4 inline-block ml-1" />
              </button>

              <button
                className={cn(
                  'rounded-lg font-bold text-[13px] border-none cursor-pointer transition-all duration-200 py-2.5',
                  (!canReroll || actionInProgress) && 'cursor-not-allowed opacity-40',
                )}
                disabled={!canReroll || actionInProgress}
                onClick={handleReroll}
                style={{
                  background: canReroll
                    ? 'linear-gradient(135deg, #A03030, #802020)'
                    : 'rgba(100,40,40,0.3)',
                  color: canReroll ? '#FFD0D0' : 'rgba(180,100,100,0.55)',
                  boxShadow: canReroll
                    ? '0 2px 12px rgba(160,48,48,0.3)'
                    : 'none',
                  border: canReroll
                    ? '2px solid rgba(200,80,80,0.3)'
                    : '2px solid rgba(100,40,40,0.15)',
                }}
              >
                {t('shop.reroll', { cost: POKER_CONFIG.REROLL_COST })}
              </button>
            </div>

            {/* Right: Joker cards centered */}
            <div className="flex-1 flex gap-3 justify-center">
              {jokerPool.map((joker) => {
                const price = JOKER_PRICE[joker.id] ?? 3
                const canAfford = shopMoney >= price
                const jokersFull = equippedJokers.length >= maxJokers
                return (
                  <JokerShopCard
                    key={joker.id}
                    joker={joker}
                    price={price}
                    canBuy={canAfford && !jokersFull}
                    onBuy={() => handleBuyJoker(joker.id)}
                  />
                )
              })}
            </div>
          </div>
        </div>

        {/* === LOWER TIER: Voucher + Research packs === */}
        <div className="flex flex-col lg:flex-row gap-4">
            {/* Voucher placeholder */}
            <div
              className="rounded-xl p-5 shrink-0 flex flex-col"
              style={{
                background: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(201,169,110,0.15)',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="text-[10px] uppercase tracking-[2px] font-bold" style={{ color: 'rgba(201,169,110,0.5)' }}>
                  Voucher
                </div>
                <div className="flex-1 h-px" style={{ background: 'rgba(201,169,110,0.1)' }} />
              </div>
              <div style={{ width: CARD_SIZES.sm.width * 3 + 16 * 2 }} />
            </div>

            {/* Research pack area */}
            <div
              className="flex-1 rounded-xl p-5"
              style={{
                background: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(100,160,120,0.15)',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="text-[10px] uppercase tracking-[2px] font-bold" style={{ color: 'rgba(180,220,190,0.5)' }}>
                  {t('shop.researchPacks')}
                </div>
                <div className="flex-1 h-px" style={{ background: 'rgba(100,160,120,0.1)' }} />
                <div className="text-[10px]" style={{ color: 'rgba(180,220,190,0.35)' }}>
                  {t('shop.packPriceInfo', { cost: POKER_CONFIG.PACK_COST, max: POKER_CONFIG.MAX_PACKS })}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
                {ALL_PACKS.map((pack, i) => {
                  const isPurchased = purchasedPacks.includes(pack.id)
                  const canBuy = !isPurchased && purchasedPacks.length < POKER_CONFIG.MAX_PACKS && shopMoney >= POKER_CONFIG.PACK_COST

                  return (
                    <PackItem
                      key={pack.id}
                      pack={pack}
                      index={i}
                      isPurchased={isPurchased}
                      canBuy={canBuy}
                      onOpen={() => canBuy && setOpenPackId(pack.id)}
                    />
                  )
                })}
              </div>
            </div>
          </div>
      </div>

      {/* Bottom: deck pile */}
      <div className="relative pb-4 z-10">
        <DeckPile deckSize={deckSize} totalSize={totalSize} />
      </div>

      {/* Pack selection modal */}
      {openPackId && (
        <PackSelectionModal
          packId={openPackId}
          onSelectCard={handleSelectCard}
          onClose={() => setOpenPackId(null)}
        />
      )}
    </PokerLayout>
  )
}

// =============================================================================
// JokerShopCard — single Joker for sale in upper tier
// =============================================================================

function JokerShopCard({
  joker,
  price,
  canBuy,
  onBuy,
}: {
  joker: Joker
  price: number
  canBuy: boolean
  onBuy: () => void
}) {
  return (
    <JokerCard
      joker={joker}
      showPrice
      price={price}
      disabled={!canBuy}
      onClick={onBuy}
    />
  )
}

// =============================================================================
// PackItem — research pack
// =============================================================================

type PackItemProps = {
  pack: CardPack
  index: number
  isPurchased: boolean
  canBuy: boolean
  onOpen: () => void
}

function PackItem({ pack, index, isPurchased, canBuy, onOpen }: PackItemProps) {
  const { t } = useTranslation()
  const theme = PACK_THEME[pack.id]
  const suitInfo = SUIT_DISPLAY[STAKEHOLDER_TO_SUIT[pack.deckSource]]
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)
  const isInteractive = canBuy && !isPurchased

  return (
    <div
      role="button"
      tabIndex={isInteractive ? 0 : -1}
      aria-disabled={!canBuy || isPurchased}
      aria-label={`${isPurchased ? t('shop.purchased') : t('shop.buy')} ${pack.name} - $${POKER_CONFIG.PACK_COST}`}
      className={cn(
        'pack-shop-item relative rounded-xl overflow-hidden transition-all duration-300',
        isInteractive && 'pack-shop-item--interactive cursor-pointer',
        isPurchased && 'cursor-default',
      )}
      style={{
        width: CARD_SIZES.sm.width,
        height: CARD_SIZES.sm.height,
        animation: `scale-in 400ms ease-out ${index * 100 + 200}ms both`,
        ['--pack-hover-glow' as string]: theme.glow,
      }}
      onClick={() => isInteractive && onOpen()}
      onKeyDown={(e) => {
        if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onOpen()
        }
      }}
      onMouseMove={(e) => {
        if (!isInteractive) return
        const rect = e.currentTarget.getBoundingClientRect()
        setMousePos({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        })
      }}
      onMouseLeave={() => setMousePos(null)}
    >
      {/* Card background with metallic gradient + inset highlights + edge pulse */}
      <div
        className="absolute inset-0 rounded-xl"
        style={{
          background: isPurchased
            ? 'linear-gradient(145deg, #2A3530, #1E2825)'
            : theme.gradient,
          border: `2px solid ${isPurchased ? 'rgba(100,160,120,0.15)' : theme.border}`,
          boxShadow: isPurchased ? 'none' : undefined,
          animation: !isPurchased ? `foil-edge-pulse ${theme.pulseDuration}s ease-in-out ${theme.pulseDelay}s infinite` : undefined,
          opacity: isPurchased ? 0.5 : 1,
          transition: 'all 0.3s ease',
        }}
      />

      {/* Metallic micro-texture (3-layer brushed metal) */}
      <div
        className="absolute inset-0 rounded-xl pointer-events-none"
        style={{
          opacity: isPurchased ? 0.02 : 0.07,
          backgroundImage: [
            `repeating-linear-gradient(${theme.textureAngles[0]}deg, transparent, transparent ${theme.textureSpacings[0] - 1}px, rgba(255,255,255,0.08) ${theme.textureSpacings[0] - 1}px, rgba(255,255,255,0.08) ${theme.textureSpacings[0]}px)`,
            `repeating-linear-gradient(${theme.textureAngles[1]}deg, transparent, transparent ${theme.textureSpacings[1] - 1}px, rgba(255,255,255,0.05) ${theme.textureSpacings[1] - 1}px, rgba(255,255,255,0.05) ${theme.textureSpacings[1]}px)`,
            `repeating-linear-gradient(${theme.textureAngles[2]}deg, transparent, transparent ${theme.textureSpacings[2] - 1}px, rgba(255,255,255,0.04) ${theme.textureSpacings[2] - 1}px, rgba(255,255,255,0.04) ${theme.textureSpacings[2]}px)`,
          ].join(', '),
        }}
      />

      {/* Foil shine sweep animation */}
      {!isPurchased && (
        <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(${theme.sweepAngle}deg, transparent 0%, transparent 35%, ${theme.shimmerColor} 45%, ${theme.shimmerColor} 55%, transparent 65%, transparent 100%)`,
              animation: `foil-shine-sweep ${theme.sweepDuration}s ease-in-out infinite`,
            }}
          />
        </div>
      )}

      {/* Mouse-following light spot */}
      {!isPurchased && mousePos && (
        <div
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            zIndex: 2,
            background: `radial-gradient(circle ${theme.spotSize}px at ${mousePos.x}% ${mousePos.y}%, ${theme.edgeHighlight}, transparent 70%)`,
            transition: 'background 50ms ease-out',
          }}
        />
      )}

      {/* Cover image */}
      <img
        src={PACK_IMAGE[pack.id]}
        alt={pack.name}
        className="absolute inset-0 w-full h-full object-cover rounded-xl pointer-events-none"
        style={{
          mixBlendMode: 'luminosity',
          opacity: isPurchased ? 0.15 : 0.45,
          transition: 'opacity 0.3s',
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-3 text-center">

        <div
          className="font-extrabold text-[14px] leading-tight tracking-wide"
          style={{
            color: isPurchased ? 'rgba(180,220,190,0.3)' : '#F0E8D8',
            fontFamily: "'Noto Serif SC', Georgia, serif",
            textShadow: isPurchased ? 'none' : '0 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {pack.name}
        </div>

        <div
          className="text-[10px] mt-1 font-semibold flex items-center justify-center gap-1"
          style={{ color: isPurchased ? 'rgba(100,160,120,0.2)' : theme.accent }}
        >
          <img src={suitInfo.iconUrl} alt={suitInfo.name} className="w-[14px] h-[14px] object-contain inline-block" style={{ opacity: isPurchased ? 0.3 : 0.8 }} />
          {suitInfo.name}
        </div>

        <div
          className="text-[9px] mt-1.5 leading-[1.4]"
          style={{ color: isPurchased ? 'rgba(100,160,120,0.15)' : 'rgba(240,232,216,0.5)' }}
        >
          {pack.description}
        </div>

        {isPurchased && (
          <div
            className="absolute top-2 right-2 text-[10px] font-bold py-0.5 px-2 rounded-full"
            style={{
              background: 'rgba(201,169,110,0.2)',
              color: '#C9A96E',
              border: '1px solid rgba(201,169,110,0.3)',
            }}
          >
            {t('shop.selected')}
          </div>
        )}
      </div>

      {!isPurchased && (
        <div
          className="absolute -top-1 left-1/2 -translate-x-1/2 rounded-b-md px-3 py-1 text-center"
          style={{
            background: canBuy ? '#C9A96E' : '#5A5A5A',
            color: canBuy ? '#2A1800' : '#999',
            fontSize: 12,
            fontWeight: 800,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            zIndex: 20,
          }}
        >
          ${POKER_CONFIG.PACK_COST}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// ShopConsumableSlots — read-only consumable display (mirrors ConsumableRow)
// =============================================================================

function ShopConsumableSlots({ consumables }: { consumables: ConsumableCard[] }) {
  const { t } = useTranslation()
  const MAX_VISIBLE = 2
  const visibleConsumables = consumables.slice(0, MAX_VISIBLE)
  const emptySlots = Math.max(0, MAX_VISIBLE - visibleConsumables.length)
  return (
    <div
      className="flex items-center gap-2 px-3 py-3 mr-4 mt-3 rounded-lg shrink-0"
      style={{
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(212,132,154,0.15)',
      }}
    >
      <div className="text-[11px] uppercase tracking-wider shrink-0 font-semibold" style={{ color: 'rgba(212,132,154,0.45)' }}>
        {t('challenge.consumables')}
      </div>
      {visibleConsumables.map(c => (
        <CardShell
          key={c.id}
          size="sm"
          borderColor="rgba(212,132,154,0.35)"
          className="shrink-0"
        >
          {/* Category dot */}
          <div
            className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full"
            style={{ background: '#D4849A' }}
          />
          {/* Icon area */}
          <div className="flex items-center justify-center pt-3 pb-1.5 px-2">
            <div
              className="w-[60px] h-[60px] rounded flex items-center justify-center"
              style={{ background: 'rgba(212,132,154,0.1)' }}
            >
              <img src={getIconifyUrl('lucide:flask-conical', 24, '#D4849A')} alt={c.name} className="w-7 h-7" />
            </div>
          </div>
          {/* Divider */}
          <div className="mx-2.5 h-px" style={{ background: 'rgba(212,132,154,0.2)' }} />
          {/* Info */}
          <div className="flex-1 flex flex-col px-2.5 py-1.5 min-h-0">
            <div className="text-[11px] font-bold leading-tight" style={{ color: '#D4849A' }}>
              {c.name}
            </div>
            <div className="text-[9px] leading-tight mt-0.5 line-clamp-2" style={{ color: 'rgba(200,190,220,0.45)' }}>
              {c.description}
            </div>
          </div>
        </CardShell>
      ))}

      {/* Empty slots — max 2 total including filled */}
      {Array.from({ length: emptySlots }).map((_, i) => (
        <div
          key={`empty-${i}`}
          className="shrink-0 rounded-[10px] border-2 border-dashed opacity-20"
          style={{ width: CARD_SIZES.sm.width, height: CARD_SIZES.sm.height, borderColor: 'rgba(212,132,154,0.3)' }}
        />
      ))}
    </div>
  )
}

// =============================================================================
// PackSelectionModal — full-screen overlay to choose a consumable from a pack
// =============================================================================

function PackSelectionModal({
  packId,
  onSelectCard,
  onClose,
}: {
  packId: PackId
  onSelectCard: (packId: PackId, consumableId: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const pack = ALL_PACKS.find(p => p.id === packId)
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Save previous focus and restore on unmount
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    return () => {
      previousFocusRef.current?.focus()
    }
  }, [])

  // Auto-focus first card on open
  useEffect(() => {
    const firstButton = modalRef.current?.querySelector('button[data-card]') as HTMLElement
    firstButton?.focus()
  }, [])

  // Lock body scroll while modal is open
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Focus trap
  const handleFocusTrap = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return
    const modal = modalRef.current
    if (!modal) return
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return
    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  if (!pack) return null

  const theme = PACK_THEME[packId]
  const suitInfo = SUIT_DISPLAY[STAKEHOLDER_TO_SUIT[pack.deckSource]]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ animation: 'fade-in 200ms ease-out' }}
      role="dialog"
      aria-modal="true"
      aria-label={pack.name}
      ref={modalRef}
      onKeyDown={handleFocusTrap}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div
        className="relative flex flex-col items-center gap-6"
        style={{ animation: 'scale-in 300ms ease-out' }}
      >
        {/* Close button */}
        <button
          type="button"
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
          onClick={onClose}
          aria-label={t('shop.clickToClose')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Pack title */}
        <div className="text-center">
          <div
            className="text-2xl font-black tracking-wide"
            style={{
              color: theme.accent,
              fontFamily: "'Noto Serif SC', Georgia, serif",
              textShadow: `0 0 20px ${theme.glow}`,
            }}
          >
            {pack.name}
          </div>
          <div className="text-[12px] mt-1 flex items-center justify-center gap-1" style={{ color: 'rgba(240,232,216,0.6)' }}>
            <img src={suitInfo.iconUrl} alt={suitInfo.name} className="w-[16px] h-[16px] object-contain inline-block" style={{ opacity: 0.7 }} />
            {suitInfo.name} · {t('shop.selectConsumable')}
          </div>
        </div>

        {/* Cards */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-5">
          {pack.cards.map((card, i) => (
            <button
              type="button"
              key={card.id}
              data-card
              className="relative rounded-xl overflow-hidden cursor-pointer text-left pack-modal-card"
              style={{
                width: 160,
                minHeight: 200,
                background: 'linear-gradient(160deg, #2A2A3A, #1E1E2C)',
                border: `2px solid rgba(255,255,255,0.1)`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                animation: `scale-in 350ms ease-out ${i * 100 + 150}ms both`,
                transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
                // CSS custom properties for hover color
                ['--pack-accent' as string]: theme.accent,
                ['--pack-glow' as string]: theme.glow,
              }}
              onClick={() => onSelectCard(pack.id, card.id)}
            >
              {/* Top accent bar */}
              <div className="h-1.5" style={{ background: theme.accent }} />

              {/* Card content */}
              <div className="p-4 flex flex-col h-full">
                {/* Effect type badge */}
                <div
                  className="text-[10px] font-bold uppercase tracking-wider py-1 px-2 rounded self-start mb-3"
                  style={{
                    background: `${theme.accent}20`,
                    color: theme.accent,
                    border: `1px solid ${theme.accent}40`,
                  }}
                >
                  {t(`effects.${card.effectType}`)}
                </div>

                {/* Name */}
                <div
                  className="text-[16px] font-bold leading-tight"
                  style={{
                    color: '#E8E0D0',
                    fontFamily: "'Noto Serif SC', Georgia, serif",
                  }}
                >
                  {card.name}
                </div>

                {/* Description */}
                <div
                  className="text-[12px] mt-2 leading-[1.5] flex-1"
                  style={{ color: 'rgba(200,190,170,0.65)' }}
                >
                  {card.description}
                </div>

                {/* Target count */}
                {card.targetCount > 0 && (
                  <div
                    className="text-[10px] mt-2 pt-2"
                    style={{
                      color: 'rgba(200,190,170,0.4)',
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    {t('shop.targetCards', { count: card.targetCount })}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Close hint */}
        <button
          type="button"
          className="text-[11px] cursor-pointer bg-transparent border-none p-0"
          style={{ color: 'rgba(255,255,255,0.6)' }}
          onClick={onClose}
        >
          {t('shop.clickToClose')}
        </button>
      </div>
    </div>
  )
}
